import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { emitAuthTrace } from '@/auth/authTraceClient';
import { resolvePostAuthRedirect, resolveSessionBootstrapState } from '@/auth/bootstrapRouting';
import { emitAuthTransitionEvent } from '@/auth/observability';
import { getManualSignOutFlag, getSignOutTransitionFlag } from '@/auth/sessionStorage';
import { hasClerkClientSession } from '@/auth/useAuth.helpers';
import type { AuthError, AuthSession } from '@/auth/types';
import type { ReactElement } from 'react';

type GuardProps = {
  session: AuthSession | null;
  loading: boolean;
  authError?: AuthError | null;
  refreshSession?: () => Promise<boolean>;
  children: ReactElement;
  featureFlagsLoading?: boolean;
  featureFlags?: Record<string, boolean> | null;
  authBypassClerk?: boolean;
};

const SESSION_HYDRATION_HOLD_TIMEOUT_MS = 5000;

const GuardFallback = ({
  pathname: _pathname,
  featureFlagsLoading: _featureFlagsLoading,
  featureFlags: _featureFlags,
}: {
  pathname: string;
  featureFlagsLoading?: boolean;
  featureFlags?: Record<string, boolean> | null;
}): ReactElement => <>{null}</>;

const hasManualSignOutState = (stateValue: unknown): boolean =>
  typeof stateValue === 'object' &&
  stateValue !== null &&
  'manualSignOut' in stateValue &&
  (stateValue as { manualSignOut?: unknown }).manualSignOut === true;

const isManualSignOutTransition = (stateValue: unknown): boolean =>
  getManualSignOutFlag() || getSignOutTransitionFlag() || hasManualSignOutState(stateValue);

export const RequireAuth = ({
  session,
  loading,
  authError,
  refreshSession,
  children,
  featureFlagsLoading,
  featureFlags,
  authBypassClerk = import.meta.env.VITE_AUTH_BYPASS_CLERK === 'true',
}: GuardProps): ReactElement => {
  if (authBypassClerk) {
    return (
      <RequireAuthBypass
        session={session}
        loading={loading}
        children={children}
        featureFlagsLoading={featureFlagsLoading}
        featureFlags={featureFlags}
      />
    );
  }
  return (
    <RequireAuthWithClerk
      session={session}
      loading={loading}
      authError={authError}
      refreshSession={refreshSession}
      children={children}
      featureFlagsLoading={featureFlagsLoading}
      featureFlags={featureFlags}
    />
  );
};

const RequireAuthBypass = ({
  session,
  loading,
  children,
  featureFlagsLoading,
  featureFlags,
}: Pick<
  GuardProps,
  'session' | 'loading' | 'children' | 'featureFlagsLoading' | 'featureFlags'
>): ReactElement => {
  const location = useLocation();
  const manualSignOutTransition = isManualSignOutTransition(location.state);
  if (loading) {
    return (
      <GuardFallback
        pathname={location.pathname}
        featureFlagsLoading={featureFlagsLoading}
        featureFlags={featureFlags}
      />
    );
  }
  if (!session) {
    if (manualSignOutTransition) {
      return <Navigate to="/sign-in" replace />;
    }
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    const redirectParam = encodeURIComponent(redirectPath);
    return <Navigate to={`/sign-in?redirect=${redirectParam}`} replace />;
  }
  return children;
};

const RequireAuthWithClerk = ({
  session,
  loading,
  authError,
  refreshSession,
  children,
  featureFlagsLoading,
  featureFlags,
}: GuardProps): ReactElement => {
  const location = useLocation();
  const { isLoaded: isClerkLoaded, isSignedIn: isClerkSignedIn } = useClerkAuth();
  const { client } = useClerk();
  const hasSignedInSignal = isClerkSignedIn ? true : hasClerkClientSession(client as unknown);
  const manualSignOutTransition = isManualSignOutTransition(location.state);
  const shouldAwaitSessionHydration =
    !session && !manualSignOutTransition && isClerkLoaded && hasSignedInSignal;
  const [hydrationHoldExpired, setHydrationHoldExpired] = useState(false);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const [recoveringSession, setRecoveringSession] = useState(false);

  useEffect(() => {
    if (!shouldAwaitSessionHydration || Boolean(authError)) {
      setHydrationHoldExpired(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHydrationHoldExpired(true);
    }, SESSION_HYDRATION_HOLD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authError, shouldAwaitSessionHydration]);

  useEffect(() => {
    if (!shouldAwaitSessionHydration) {
      setRecoveryAttempted(false);
      setRecoveringSession(false);
    }
  }, [shouldAwaitSessionHydration]);

  const shouldAttemptRecovery =
    shouldAwaitSessionHydration &&
    hydrationHoldExpired &&
    !authError &&
    !recoveryAttempted &&
    typeof refreshSession === 'function';

  useEffect(() => {
    if (!shouldAttemptRecovery) {
      return;
    }
    let cancelled = false;
    setRecoveringSession(true);
    void refreshSession()
      .catch(() => false)
      .finally(() => {
        if (cancelled) {
          return;
        }
        setRecoveringSession(false);
        setRecoveryAttempted(true);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshSession, shouldAttemptRecovery]);

  const shouldHoldForHydration = shouldAwaitSessionHydration && !authError && !hydrationHoldExpired;
  const shouldShowGuardFallback =
    (!session && loading) || shouldHoldForHydration || recoveringSession || shouldAttemptRecovery;
  if (shouldShowGuardFallback) {
    return (
      <GuardFallback
        pathname={location.pathname}
        featureFlagsLoading={featureFlagsLoading}
        featureFlags={featureFlags}
      />
    );
  }
  if (!session) {
    if (manualSignOutTransition) {
      return <Navigate to="/sign-in" replace />;
    }
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    const redirectParam = encodeURIComponent(redirectPath);
    return <Navigate to={`/sign-in?redirect=${redirectParam}`} replace />;
  }
  return children;
};

export const RequireOnboarding = ({
  session,
  children,
}: {
  session: AuthSession | null;
  children: ReactElement;
}): ReactElement => {
  const location = useLocation();
  const stateValue = location.state as unknown;
  const suppressOnboardingRedirect =
    typeof stateValue === 'object' &&
    stateValue !== null &&
    'suppressOnboardingRedirect' in stateValue &&
    (stateValue as { suppressOnboardingRedirect?: unknown }).suppressOnboardingRedirect === true;
  const emittedRedirectRef = useRef<string | null>(null);
  if (!session) return children;
  const intendedPath = `${location.pathname}${location.search}${location.hash}`;
  let destination = resolvePostAuthRedirect({
    intendedPath,
    bootstrapState: resolveSessionBootstrapState(session),
    fallback: '/execution-runs',
  });
  if (suppressOnboardingRedirect && destination === '/onboarding') {
    destination = '/execution-runs';
  }
  if (destination !== intendedPath) {
    emitAuthTrace({
      event: 'require_onboarding_redirect',
      userId: session.userId,
      detail: {
        from: intendedPath,
        to: destination,
        onboardingCompleted: session.onboardingCompleted === true,
      },
    });
    const redirectKey = `${intendedPath}->${destination}`;
    if (emittedRedirectRef.current !== redirectKey) {
      emittedRedirectRef.current = redirectKey;
      emitAuthTransitionEvent('onboarding_redirected', {
        from: intendedPath,
        to: destination,
        reason:
          destination === '/onboarding' ? 'onboarding_incomplete' : 'onboarding_already_completed',
        userId: session.userId,
      });
    }
    return <Navigate to={destination} replace />;
  }
  emittedRedirectRef.current = null;
  return children;
};

export const RequireFeatureFlag = ({
  featureFlag,
  featureFlagsLoading,
  featureFlags,
  children,
}: {
  featureFlag?: string;
  featureFlagsLoading?: boolean;
  featureFlags?: Record<string, boolean> | null;
  children: ReactElement;
}): ReactElement => {
  const authBypassClerkEnabled = import.meta.env.VITE_AUTH_BYPASS_CLERK === 'true';
  if (authBypassClerkEnabled) {
    return children;
  }
  const location = useLocation();
  if (!featureFlag) return children;
  if (featureFlagsLoading) {
    return (
      <GuardFallback
        pathname={location.pathname}
        featureFlagsLoading={featureFlagsLoading}
        featureFlags={featureFlags}
      />
    );
  }
  if (featureFlags == null || !(featureFlag in featureFlags)) {
    return (
      <GuardFallback
        pathname={location.pathname}
        featureFlagsLoading={featureFlagsLoading}
        featureFlags={featureFlags}
      />
    );
  }
  const enabled = featureFlags[featureFlag];
  if (!enabled) {
    return <Navigate to="/execution-runs" replace />;
  }
  return children;
};

export const RequireAdminAccess = ({
  canAccess,
  loading = false,
  children,
}: {
  canAccess: boolean;
  loading?: boolean;
  children: ReactElement;
}): ReactElement => {
  const location = useLocation();
  if (loading) {
    return <GuardFallback pathname={location.pathname} />;
  }
  if (!canAccess) {
    return <Navigate to="/" replace />;
  }
  return children;
};

export const RequireSuperuserAccess = ({
  canAccess,
  children,
}: {
  canAccess: boolean;
  children: ReactElement;
}): ReactElement => {
  if (!canAccess) {
    return <Navigate to="/" replace />;
  }
  return children;
};
